import { GrcObjectDto, useApi } from '@issuemanager/api-client';
import {
  useAppConfig,
  ExtendedAxiosError,
} from '@issuemanager/apps/client/hooks';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

const MOCK_GRC_OBJECTS: GrcObjectDto[] = [
  { id: '1', name: 'Q4 Financial Audit Evidence' },
  { id: '2', name: 'Compliance Review Documentation' },
  { id: '3', name: 'IT Security Assessment Results' },
  { id: '4', name: 'Internal Audit Report - FY2024' },
  { id: '5', name: 'SOX Compliance Evidence Package' },
  { id: '6', name: 'Data Privacy Impact Assessment' },
  { id: '7', name: 'Third-Party Vendor Security Audit' },
  { id: '8', name: 'Business Continuity Test Results' },
  { id: '9', name: 'Risk Management Framework Documentation' },
  { id: '10', name: 'Access Control Review Evidence' },
  { id: '11', name: 'Cybersecurity Vulnerability Assessment' },
  { id: '12', name: 'Annual Financial Statement Review' },
  { id: '13', name: 'Disaster Recovery Plan Testing' },
  { id: '14', name: 'Employee Training Compliance Records' },
  { id: '15', name: 'Vendor Contract Review Evidence' },
  { id: '16', name: 'Cloud Infrastructure Security Audit' },
  { id: '17', name: 'GDPR Data Processing Documentation' },
  { id: '18', name: 'Network Security Assessment Q2' },
  { id: '19', name: 'Physical Security Controls Review' },
  { id: '20', name: 'Identity and Access Management Audit' },
  { id: '21', name: 'Change Management Process Evidence' },
  { id: '22', name: 'Patch Management Compliance Records' },
  { id: '23', name: 'Backup and Recovery Test Results' },
  { id: '24', name: 'Encryption Implementation Evidence' },
  { id: '25', name: 'Security Awareness Training Materials' },
  { id: '26', name: 'Incident Response Plan Documentation' },
  { id: '27', name: 'Third-Party Risk Assessment Report' },
  { id: '28', name: 'Data Classification Policy Evidence' },
  { id: '29', name: 'Access Review Log Q3' },
  { id: '30', name: 'Vulnerability Scanning Results' },
];

interface PaginatedGrcObjects {
  data: GrcObjectDto[];
  meta: {
    page: number;
    limit: number;
    itemCount: number;
    pageCount: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

const fetchMockGrcObjects = (
  searchTerm?: string,
  page = 0,
  limit = 10,
): Promise<PaginatedGrcObjects> => {
  const filteredItems = searchTerm
    ? MOCK_GRC_OBJECTS.filter((obj) =>
        obj.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : MOCK_GRC_OBJECTS;

  const itemCount = filteredItems.length;
  const pageCount = Math.ceil(itemCount / limit);
  const start = page * limit;
  const end = start + limit;
  const pagedItems = filteredItems.slice(start, end);

  const meta = {
    page,
    limit,
    itemCount,
    pageCount,
    hasPreviousPage: page > 0,
    hasNextPage: page < pageCount - 1,
  };

  return Promise.resolve({
    data: pagedItems,
    meta,
  });
};

export interface UseInfiniteGrcObjectsParams {
  searchTerm?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
  handleError?: boolean;
}

export function useInfiniteGrcObjects(
  params: UseInfiniteGrcObjectsParams = {},
): ReturnType<
  typeof useInfiniteQuery<
    PaginatedGrcObjects,
    ExtendedAxiosError,
    InfiniteData<PaginatedGrcObjects>,
    string[],
    number
  >
> {
  const {
    searchTerm,
    page = 0,
    limit = 10,
    enabled = true,
    handleError = false,
  } = params;
  const api = useApi();
  const appConfig = useAppConfig();

  const queryKey = ['grc-objects', searchTerm, page, limit, enabled];

  return useInfiniteQuery<
    PaginatedGrcObjects,
    ExtendedAxiosError,
    InfiniteData<PaginatedGrcObjects>,
    typeof queryKey,
    number
  >({
    queryKey,
    queryFn: async ({ pageParam }) => {
      if (appConfig.isLocalDev) {
        return fetchMockGrcObjects(searchTerm, pageParam, limit);
      }

      const response = await api.grcObjectsApi.getGrcObjects(
        { objectType: 'evidence', searchTerm },
        pageParam,
        limit,
      );

      return {
        data: response.data.data,
        meta: {
          page: response.data.meta.page,
          limit: response.data.meta.limit,
          itemCount: response.data.meta.itemCount,
          pageCount: response.data.meta.pageCount,
          hasPreviousPage: response.data.meta.hasPreviousPage,
          hasNextPage: response.data.meta.hasNextPage,
        },
      };
    },
    enabled,
    initialPageParam: page,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
    meta: {
      handleError,
    },
  });
}
